import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import FormInput from '../components/inputs/FormInput'
import DropdownInput from '../components/inputs/DropdownInput'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const NewUserHarborTechnician = () => {
    const [harbors, setHarbors] = useState([])

    const [selectedHarbor, setSelectedHarbor] = useState(null)
    const [selectedHarborError, setSelectedHarborError] = useState(null)

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(null)

    const [username, setUsername] = useState('')
    const [usernameError, setUsernameError] = useState(null)

    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role === Constants.Roles.ADMINIT) {
                getDropdowns()
            } else if (loggedUser.role === Constants.Roles.PORTADMIN) {
                getUserHarbor()
            } else {
                navigate('/')
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get user harbor request
    const getUserHarbor = () => {
        fetch('/api/Backoffice/GetHarbor', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                const harbor = {
                    value: jsonRes.harborId,
                    label: jsonRes.harborName
                }

                setSelectedHarbor(harbor)

                getDropdowns()
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getUserHarborError)

                    setSelectedHarbor(null)
                }, 1000)
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                setSelectedHarbor(null)
            }, 1000)
        })
    }

    // Handle get dropdowns
    const getDropdowns = () => {
        fetch('/api/Backoffice/GetDocapescaHarbors', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
    
                    const arrayHarbors = jsonRes.map((option) => ({value: option.id, label: option.name }))

                    setHarbors(arrayHarbors)
                } else {
                    setTimeout(() => {
                        setIsLoading(false)
                        showErrorToast(strings.getDropdownsError)
    
                        setHarbors([])
                    }, 1000)
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setHarbors([])
            }, 1000)   
        })
    }

    // Handle changes in harbor dropdown
    const onChangeSelectedHarbor = (item) => {
        setSelectedHarbor(item)
        setSelectedHarborError(null)
    }

    // Handle changes in name input text
    const onChangeName = (nameText) => {
        setNameError(null)
        setName(nameText.target.value)
    }

    // Handle changes in username input text
    const onChangeUsername = (usernameText) => {
        setUsernameError(null)
        setUsername(usernameText.target.value)
    }

    // Handle changes in email input text
    const onChangeEmail = (emailText) => {
        setEmailError(null)
        setEmail(emailText.target.value)
    }

    // Check if email is valid
    const isValidEmail = (email) => {
        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        return emailPattern.test(email)
    }

    // Check if all fields are valid and create the user harbor technician
    const createHarborTechnician = () => {
        let hasErrors = false

        if (email.trim().length === 0) {
            setEmailError(strings.insertEmailError)
            hasErrors = true
        } else if (!isValidEmail(email)) {
            hasErrors = true
            setEmailError(strings.invalidEmail)
        }
    
        if (name.trim().length === 0) {
            hasErrors = true
            setNameError(strings.insertNameError)
        }

        if (username.trim().length === 0) {
            hasErrors = true
            setUsernameError(strings.insertUsernameError)
        }

        if(!selectedHarbor) {
            hasErrors = true
            setSelectedHarborError(strings.selectAffectedShipyardError)
        }

        if (!hasErrors) {
            setIsLoading(true)

            const data = {
                HarborId: selectedHarbor.value,
                Name: name,
                Username: username,
                Email: email,
                RoleId: Constants.Roles.PORTTECHNICIAN
            }

            // Create user port technician
            fetch('/api/Backoffice/CreateUser', {
                method:'POST',
                headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json; charset=UTF-8',
                  'Authorization': 'Bearer ' + loggedUser.jwtToken
                }, 
                mode: 'cors',
                body: JSON.stringify(data)
            }).then(async response => {
                setTimeout(() => {
                    setIsLoading(false)

                    if (response.status === 200) {
                        navigate('/backoffice-users')
                    } else if (response.status === 409){
                        showErrorToast(strings.createAccountEmailError)
                    } else {
                        showErrorToast(strings.createUserError)
                    }
                }, 1000)

            }).catch((error) => {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(error)
                }, 1000)
            })
        }
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='flex flex-col px-[12.5rem] h-screen'>

                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.userManagement} / {strings.newUserTechnician}</p>
                        </div>

                        <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>

                        <div className='flex flex-row mt-6 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <p className='font-montserrat font-semibold text-sm'>{strings.affectedShipyard}</p>
                                <DropdownInput 
                                    options={harbors}
                                    value={selectedHarbor}
                                    onChange={onChangeSelectedHarbor}
                                    error={selectedHarborError}
                                    disabled={loggedUser.role === Constants.Roles.PORTADMIN ? true : false}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedHarborError ? selectedHarborError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='name' className='font-montserrat font-semibold text-sm'>{strings.name}</label>
                                <FormInput 
                                    id='name' 
                                    type='text'
                                    value={name}
                                    placeholder={strings.insertName}
                                    onChange={onChangeName}
                                    error={nameError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nameError ? nameError : ''}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-6 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='username' className='font-montserrat font-semibold text-sm'>{strings.username}</label>
                                <FormInput 
                                    id='username' 
                                    type='text'
                                    value={username}
                                    placeholder={strings.insertUsername}
                                    onChange={onChangeUsername}
                                    error={usernameError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{usernameError ? usernameError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='email' className='font-montserrat font-semibold text-sm'>{strings.email}</label>
                                <FormInput 
                                    id='email' 
                                    type='email'
                                    value={email}
                                    placeholder={strings.insertEmail}
                                    onChange={onChangeEmail}
                                    error={emailError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{emailError ? emailError : ''}</p>
                            </div>
                        </div>

                        <div className='flex mt-8 w-60'>
                            <RoundedButton 
                                onClick={createHarborTechnician}
                                text={strings.createUser}
                                height={48}
                                bgColor='#007EB4'
                            />
                        </div>
                    </>
                }
                
            </div>

            <ToastContainer />
        </>
    )
}

export default NewUserHarborTechnician